'use client';

import { useEffect, useState } from 'react';
import { useBreakpointValue, Button, LucideIcon } from '@skin-seoul/design-system';
import { LoggingImpression, LoggingClick, useCallbackRef } from '@skin-seoul/utils';
import {
  useReviewService,
  useAnalyticsEvent,
  trackReviewInteraction,
  trackLoadMoreReviews,
  trackReviewCardExpanded,
  trackReviewFilterUsed,
  trackReviewImageSwiped,
  type ReviewEventBase,
  type ReviewFilterType,
} from '@skin-seoul/service';
import type { ProductDetail, Review as ServiceReview } from '@skin-seoul/service';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import LoadMoreButton from './components/LoadMoreButton';

import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';
import ReviewDetailModal from './components/ReviewDetailModal';

export { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
export { ReviewWritePcModal } from './components/ReviewWritePcModal';

import ReviewImageGallery from './components/ReviewImageGallery';
import ReviewEmpty from './components/ReviewEmpty';
import { cleanSkinData } from '@skin-seoul/service/src/utils/review/taxonomyUtils';
import type { ReviewFormData } from '@skin-seoul/service';
import reviewPreviewDesktop from './assets/images/review-preview-desktop.png';
import reviewPreviewMobile from './assets/images/review-preview-mobile.png';

const LOAD_MORE_REVIEW_LIMIT_COUNT = 8;

const Container = ({ children, id, className }: { children: React.ReactNode; id?: string; className?: string }) => (
  <div
    id={id}
    className={`w-full bg-white box-border max-w-full tablet:max-w-[1280px] mx-0 tablet:mx-auto ${className || ''}`}
  >
    {children}
  </div>
);

const Divider = () => <div className="border-none h-px bg-[#e2e2e2] my-6 tablet:my-8" />;

export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
  reviewId: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

function mapServiceReviewToReview(r: ServiceReview): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '',
      name: r.name,
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: r.modifiedAt,
    likeCount: 0,
    skinType: cleanSkinData(r.skinType),
    skinConcern: cleanSkinData(r.skinConcern),
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  };
}

interface ReviewSectionProps {
  product: ProductDetail;
  locale?: string;
}

export const ReviewSection = ({ product, locale }: ReviewSectionProps) => {
  const [displayedReviewsCount, setDisplayedReviewsCount] = useState(LOAD_MORE_REVIEW_LIMIT_COUNT);
  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [imageGalleryInitialState, setImageGalleryInitialState] = useState<{
    selectedReview: Review | null;
    selectedImageId: string | null;
  }>({
    selectedReview: null,
    selectedImageId: null,
  });

  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useBreakpointValue({ base: false, tablet: true });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAiReviews, setShowAiReviews] = useState(false);

  const reviewService = useReviewService();
  const analytics = useAnalyticsEvent();
  const { useGetProductReviews, useGetAIProductReviews, useCreateReview } = reviewService;

  const productIdForReview = product.productId.toString();

  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  const {
    data: reviewsRaw = [],
    isLoading: isReviewsLoading,
    isSuccess: isReviewsSuccess,
  } = useGetProductReviews(Number(product.productId), {
    locale,
  });
  const { data: aiReviewsRaw = [], isFetching: isFetchingAIReviews } = useGetAIProductReviews(
    Number(product.productId),
    { locale },
  );

  const reviews = reviewsRaw.map(mapServiceReviewToReview);
  const aiReviews = aiReviewsRaw.map(mapServiceReviewToReview);

  const customerReviewImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  );

  const sortedReviews = (() => {
    switch (sort) {
      case 'newest':
        return [...reviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'oldest':
        return [...reviews].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'highest':
        return [...reviews].sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return [...reviews].sort((a, b) => a.rating - b.rating);
      default:
        return reviews;
    }
  })();

  const sortedAndFilteredReviews = sortedReviews
    .filter((review) => skinTypes.length === 0 || skinTypes.some((type) => review.skinType.includes(type)))
    .filter(
      (review) => skinConcerns.length === 0 || skinConcerns.some((concern) => review.skinConcern.includes(concern)),
    );

  const totalReviewsLength = reviews.length;

  const displayedReviews = sortedAndFilteredReviews.slice(0, displayedReviewsCount);

  const hasMoreReviews = displayedReviewsCount < sortedAndFilteredReviews.length;

  /**
   * Review-list analytics properties (SSD-5573). Every event below fires from a control
   * that only renders once reviews are on screen, so the count is known here.
   */
  const reviewEventBase: ReviewEventBase = {
    productId: productIdForReview,
    block: 'review_list',
    // Unknown until the query succeeds — an empty list during loading or after an
    // error is not evidence that the product has no first-party reviews.
    hasFirstPartyReview: isReviewsSuccess ? reviews.length > 0 : undefined,
  };

  const hasShownAllReviews = !hasMoreReviews;

  const handleLoadMore = useCallbackRef(() => {
    // The page about to be shown: the initial render is page 1, so each press adds one.
    trackLoadMoreReviews(
      analytics.trackCustom,
      reviewEventBase,
      Math.floor(displayedReviewsCount / LOAD_MORE_REVIEW_LIMIT_COUNT) + 1,
    );
    setDisplayedReviewsCount((prev) => prev + LOAD_MORE_REVIEW_LIMIT_COUNT);
  });

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedReviewsCount(LOAD_MORE_REVIEW_LIMIT_COUNT);
  }, [sort, skinTypes, skinConcerns]);

  const averageRating =
    totalReviewsLength === 0 ? 0 : reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength;

  const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const hasReviews = reviews.length > 0;
  const hasAiReviews = aiReviews.length > 0;

  const canShowAIButton =
    !isReviewsLoading && ((hasReviews && hasShownAllReviews) || !hasReviews) && hasAiReviews && !showAiReviews;

  const handleShowAIReviews = useCallbackRef(() => {
    setShowAiReviews(true);
  });

  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleOpenWriteModal = () => {
    trackReviewInteraction(analytics.trackCustom, 'write_review_clicked', { product_id: productIdForReview });
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleCloseWriteModal = useCallbackRef(() => setIsPcWriteModalOpen(false));

  const handleCloseMobileWriteModal = useCallbackRef(() => setIsMobileWriteOpen(false));

  const handleSubmit = useCallbackRef((values: ReviewFormData) => {
    trackReviewInteraction(analytics.trackCustom, 'submit_review_clicked', { product_id: productIdForReview });
    return new Promise<void>((resolve, reject) => {
      createReview(
        {
          name: values.nickname,
          rating: values.rating,
          body: values.content,
          productId: Number(product.productId),
          title: values.title,
          files: values.images,
          skinType: [values.skinType],
          skinConcern: [values.skinConcern],
          email: values.email,
        },
        {
          onSuccess: () => {
            trackReviewInteraction(analytics.trackCustom, 'review_submitted', {
              product_id: productIdForReview,
              rating: values.rating,
              image_count: values.images?.length ?? 0,
            });
            resolve();
          },
          onError: (error) => {
            reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
          },
        },
      );
    });
  });

  const trackFilter = (filterType: ReviewFilterType, filterValue: string) =>
    trackReviewFilterUsed(analytics.trackCustom, reviewEventBase, filterType, filterValue);

  const handleSortChange = (sortBy: string) => {
    trackFilter('sort_by', sortBy);
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    // Multi-select: the applied set is one filter value, so it is joined rather than
    // emitted per option — one interaction, one event.
    trackFilter('skin_type', skinTypes.join(','));
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    trackFilter('skin_concern', skinConcerns.join(','));
    setSkinConcerns(skinConcerns);
  };

  const handleReviewImageSwiped = useCallbackRef((review: Review, toIndex: number) => {
    trackReviewImageSwiped(analytics.trackCustom, reviewEventBase, review.id, toIndex);
  });

  const handleImageClick = useCallbackRef((image: ReviewImage, _index: number) => {
    const review = reviews.find((r) => r.images?.some((img) => img.id === image.id));
    if (review) {
      setIsImageGalleryOpen(true);
      setImageGalleryInitialState({
        selectedReview: review,
        selectedImageId: image.id,
      });
    }
  });

  const handleViewAllImages = useCallbackRef(() => {
    trackReviewInteraction(analytics.trackCustom, 'review_images_view_all_clicked', { product_id: productIdForReview });
    setIsImageGalleryOpen(true);
  });

  const handleCloseImageGallery = useCallbackRef(() => {
    setIsImageGalleryOpen(false);
    setImageGalleryInitialState({
      selectedReview: null,
      selectedImageId: null,
    });
  });

  const handleViewMore = useCallbackRef((review: Review) => {
    trackReviewCardExpanded(analytics.trackCustom, reviewEventBase, review.id);
    setIsImageGalleryOpen(true);
    const firstImageId = review.images && review.images.length > 0 ? review.images[0].id : null;
    setImageGalleryInitialState({
      selectedReview: review,
      selectedImageId: firstImageId,
    });
  });

  // AI reviews are shown all at once, no pagination

  return (
    <Container id="judgeme_product_reviews">
      <div className="flex flex-col items-start gap-4 mb-6 tablet:mb-4" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex flex-col items-start gap-6">
          <div className="flex flex-col items-start gap-2">
            <p className="text-paragraph-medium tablet:text-heading-h6 font-medium text-neutral-800 text-left whitespace-nowrap">
              Ratings & Reviews
            </p>
            <div className="flex flex-row items-center gap-1">
              <LucideIcon name="Info" className="text-neutral-500 w-4 h-4 tablet:w-5 tablet:h-5" />
              <p className="text-paragraph-xsmall tablet:text-paragraph-small font-medium text-neutral-500">
                All the reviews in SkinSeoul are verified.
              </p>
            </div>
          </div>
          {!hasReviews && !hasAiReviews && <ReviewEmpty />}
        </div>
        <Button variant="primary" onClick={handleOpenWriteModal} disabled={isCreatingReview}>
          Write a review
        </Button>
      </div>
      {hasReviews && (
        <>
          <Divider />
          <ReviewHeader
            averageRating={averageRating}
            totalReviews={totalReviewsLength}
            ratingDistribution={ratingDistribution}
            customerImages={customerReviewImages}
            reviews={reviews}
            onViewAllImages={handleViewAllImages}
            onWriteReview={handleOpenWriteModal}
            isWriteReviewDisabled={isCreatingReview}
          />
        </>
      )}

      {hasReviews && !isReviewsLoading && (
        <>
          <div className="mt-4">
            <ReviewImageGallery images={customerReviewImages} onViewAll={handleViewAllImages} />
          </div>
          <Divider />
          <div className="flex flex-col items-start gap-3 mt-6 tablet:mt-8">
            <p className="text-label-medium tablet:text-label-large font-medium">Filters</p>
            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />
          </div>
        </>
      )}

      {!isReviewsLoading && (hasReviews || hasAiReviews) && (
        <div className="flex flex-col gap-[14px] items-start mt-6 tablet:mt-8">
          <p className="text-label-medium tablet:text-heading-h6 font-medium">Reviews</p>
          {(hasReviews || (hasAiReviews && showAiReviews)) && (
            <div className="flex flex-col gap-4 self-start w-full">
              {displayedReviews.map((review, columnIndex, columnArray) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onImageClick={handleImageClick}
                  onViewMore={handleViewMore}
                  onImageIndexChange={handleReviewImageSwiped}
                  isLastInColumn={columnIndex === columnArray.length - 1}
                />
              ))}
              {hasAiReviews && showAiReviews && (
                <LoggingImpression
                  logName="ai_review_section_expand"
                  params={{
                    productId: product.productId,
                    name: product.name,
                    price:
                      ('salePrice' in product && product.salePrice) ||
                      ('originalPrice' in product && product.originalPrice) ||
                      0,
                    categories: product.category,
                    variantId: product.productId,
                    itemId: product.productId,
                    pageType: 'Product Detail Page',
                    section: 'AI Review Widget',
                    metadata: {
                      source: 'SkinSight™',
                      component: 'AI Review Section',
                      loadType: 'inline_replacement',
                    },
                  }}
                >
                  <div className="flex flex-col gap-4 self-start w-full">
                    {aiReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={`ai_${review.id}`}
                        review={review}
                        onImageClick={handleImageClick}
                        onViewMore={handleViewMore}
                        onImageIndexChange={handleReviewImageSwiped}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </div>
                </LoggingImpression>
              )}
            </div>
          )}
        </div>
      )}

      {!isReviewsLoading && hasReviews && hasMoreReviews && (
        <div className="flex items-center justify-center w-full mt-6 tablet:mt-8">
          <LoadMoreButton onClick={handleLoadMore} />
        </div>
      )}
      {canShowAIButton && !hasReviews && (
        <>
          <img
            src={reviewPreviewDesktop.src}
            alt="AI Review Preview"
            className="w-full h-auto object-cover hidden tablet:block"
          />
          <img
            src={reviewPreviewMobile.src}
            alt="AI Review Preview"
            className="w-full h-auto object-cover block tablet:hidden"
          />
        </>
      )}

      {canShowAIButton && (
        <LoggingImpression
          logName="ai_review_button_impression"
          params={{
            productId: product.productId,
            name: product.name,
            price:
              ('salePrice' in product && product.salePrice) ||
              ('originalPrice' in product && product.originalPrice) ||
              0,
            categories: product.category,
            variantId: product.productId,
            itemId: product.productId,
            pageType: 'Product Detail Page',
            section: 'AI Review Widget',
            metadata: {
              source: 'SkinSight™',
              component: 'AI Review Button',
            },
          }}
        >
          <div className="flex flex-col items-center justify-center w-full mt-4 gap-2">
            <LoggingClick
              logName="ai_review_button_click"
              params={{
                productId: product.productId,
                name: product.name,
                price:
                  ('salePrice' in product && product.salePrice) ||
                  ('originalPrice' in product && product.originalPrice) ||
                  0,
                categories: product.category,
                variantId: product.productId,
                itemId: product.productId,
                pageType: 'Product Detail Page',
                section: 'AI Review Widget',
                metadata: {
                  source: 'SkinSight™',
                  component: 'AI Review Button',
                },
                buttonText: 'View SkinSight™ Curated Reviews',
              }}
            >
              <Button
                className="w-full bg-generic-0 text-primary-500 rounded-[8px] border font-normal text-label-medium"
                onClick={handleShowAIReviews}
                disabled={isFetchingAIReviews}
                variant="outline"
              >
                {isFetchingAIReviews ? 'Loading View SkinSight™ Curated Reviews' : 'View SkinSight™ Curated Reviews'}
              </Button>
            </LoggingClick>
            <div className="flex flex-row gap-1 items-center">
              <LucideIcon name="Info" className="text-neutral-500 shrink-0" size={16} />
              <p className="text-paragraph-xsmall tablet:text-paragraph-small font-normal text-neutral-500">
                SkinSight™ is our proprietary AI ranking system built from millions of real Korean skincare reviews.
              </p>
            </div>
          </div>
        </LoggingImpression>
      )}

      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      <ReviewDetailModal
        isOpen={isImageGalleryOpen}
        onClose={handleCloseImageGallery}
        images={customerReviewImages}
        reviews={reviews}
        initialSelectedReview={imageGalleryInitialState.selectedReview}
        initialSelectedImageId={imageGalleryInitialState.selectedImageId}
      />
    </Container>
  );
};

export default ReviewSection;
